"use client";

import { RichText, type RichTextProps } from "basehub/react-rich-text";
import * as React from "react";

import { Button } from "@ui/components/ui/button";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { flattenRichTextNodes, getOffsetTop } from "./utils";

export type TocProps = RichTextProps & {
  currentSectionId?: string;
  children: React.ReactNode;
};

export const Toc = ({ blocks, children = [] }: TocProps) => {
  const [currentSectionId, setCurrentSectionId] = useState("");
  const disabled = useRef(false);
  const tocRef = useRef<HTMLElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const setHighlightedSection = useCallback((sectionId: string) => {
    setCurrentSectionId((prevState) => {
      if (disabled.current) return prevState;
      return sectionId;
    });
  }, []);

  const headingListLength = flattenRichTextNodes(children as unknown[]).filter(
    (item) => {
      // @ts-expect-error item does not...
      if (!("marks" in item)) return false;
      // @ts-expect-error item does not...
      return item?.marks?.some(
        (mark: { type: string }) => mark.type === "link",
      );
    },
  ).length;

  React.useEffect(() => {
    const handleScroll = () => {
      let lastActive = "";
      const headingList = Array.from(
        document.querySelectorAll<HTMLHeadingElement>("h1, h2, h3, h4, h5, h6"),
      ).filter((node) => {
        if (node.dataset.type === "stepper-checkpoint") return false;
        return node.id;
      });

      if (
        window.innerHeight + Math.round(window.scrollY) >=
          document.documentElement.scrollHeight &&
        !window.location.hash
      ) {
        lastActive = headingList[headingListLength - 1]?.id ?? "";
      } else {
        for (const heading of headingList) {
          const element = document.getElementById(heading.id);
          const offsetTop = getOffsetTop(element);

          if (
            element &&
            offsetTop <= window.innerHeight / 2 + Math.round(window.scrollY)
          ) {
            lastActive = heading.id;
          } else {
            break;
          }
        }
      }

      setHighlightedSection(lastActive);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [headingListLength, setHighlightedSection]);

  const handleScroll = React.useCallback(() => {
    const scrollY = Math.round(window.scrollY);
    if (scrollY > 1000) {
      setShowBackToTop(true);
    } else {
      setShowBackToTop(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <aside ref={tocRef} className="sticky top-28 h-max hidden lg:block">
      {!!(children as unknown[]).length && (
        <>
          <div className="text-slate-200 text-sm font-medium mb-3">
            On this page
          </div>
          <RichText
            blocks={blocks}
            components={{
              ol: ({ children, ...props }) => <ol {...props}>{children}</ol>,
              li: ({ children }) => <li className="my-2">{children}</li>,
              a: ({ href, children }) => {
                if (!children) return <></>;
                const childrenAsString = children.toString();

                return (
                  <a
                    href={href}
                    data-active={href === `#${currentSectionId}`}
                    onClick={() => {
                      setHighlightedSection(href.split("#")[1] as string);
                      disabled.current = true;
                      setTimeout(() => {
                        disabled.current = false;
                      }, 400);
                    }}
                    className="text-slate-500 text-sm data-[active=true]:text-slate-400"
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                      }}
                    >
                      {childrenAsString}
                    </span>
                  </a>
                );
              },
            }}
          >
            {children}
          </RichText>
          <AnimatePresence>
            {showBackToTop && (
              <motion.div
                className="mt-4"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
              >
                <Button
                  variant="ghost"
                  className="w-max text-slate-400 gap-2 px-0 h-max hover:bg-transparent"
                  size="sm"
                  onClick={() => {
                    document.documentElement.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  <div className="size-[18px] border rounded-md flex items-center justify-center">
                    <ArrowUpIcon className="size-[10px]" />
                  </div>
                  Back to top
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </aside>
  );
};
